<template>
  <div :class="['auto-email-report-page', { 'rmr-page-shell': !embedded, 'pa-0': embedded }]">
    <!-- Header Hero Banner (RMR Design System Style) -->
    <rmr-page-hero
      v-if="!embedded"
      title="Rapport"
      title-accent="Mail Auto"
      subtitle="Gestion des destinataires, périmètres & options d'envoi automatique"
      :icon="icons.mdiEmailSync"
      class="mb-6"
    />

    <!-- Main Configuration Card: Spreadsheet Link & Send Settings -->
    <v-card elevation="2" class="mb-6 rounded-xl rmr-card rmr-card-purple">
      <v-card-title class="d-flex align-center justify-space-between py-4 px-6 border-bottom">
        <div class="d-flex align-center">
          <v-icon class="mr-2" color="indigo darken-2">{{ icons.mdiLinkVariant }}</v-icon>
          <span class="text-subtitle-1 font-weight-bold text-uppercase grey--text text--darken-2">Fichier Spreadsheet & Options d'Envoi</span>
        </div>
        <div class="badge-icon-circle bg-indigo-light">
          <v-icon color="indigo darken-2" small>{{ icons.mdiGoogleSpreadsheet }}</v-icon>
        </div>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-row class="align-center">
          <!-- Spreadsheet Link Input & Sync Button -->
          <v-col cols="12" md="7" class="d-flex align-center">
            <div class="flex-grow-1">
              <rmr-text-field
                v-model="config.spreadsheet_url"
                label="Lien du fichier Spreadsheet"
                placeholder="https://docs.google.com/spreadsheets/d/..."
                :prepend-inner-icon="icons.mdiGoogleSpreadsheet"
              />
            </div>
            <v-expand-x-transition>
              <div v-if="isSpreadsheetUrlDirty" class="d-flex align-center ml-3">
                <rmr-icon-button
                  :icon="icons.mdiCheck"
                  variant="success"
                  tooltip="Enregistrer la modification du lien"
                  :loading="savingSpreadsheetUrl"
                  class="mr-1"
                  @click="saveSpreadsheetUrl"
                />
                <rmr-icon-button
                  :icon="icons.mdiClose"
                  variant="delete"
                  tooltip="Annuler"
                  @click="cancelSpreadsheetUrlEdit"
                />
              </div>
            </v-expand-x-transition>
            <div v-if="!isSpreadsheetUrlDirty" class="ml-3 d-flex align-center">
              <rmr-action-button
                label="Synchroniser les données"
                variant="primary"
                size="sm"
                :icon="icons.mdiRefresh"
                :loading="syncingSpreadsheet"
                @click="openConfirmSyncModal"
              />
            </div>
          </v-col>
          <v-col cols="12" md="5" class="d-flex align-center justify-end">
            <span v-if="config.updated_at || config.last_sync_at" class="text-caption text--secondary">
              Lien mis à jour le : {{ formatDate(config.updated_at || config.last_sync_at) }}
            </span>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Quota Progress Bar & Indicator -->
        <div class="pa-4 bg-light-purple rounded-lg border mb-4">
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="d-flex align-center">
              <v-icon small color="indigo darken-2" class="mr-2">{{ icons.mdiEmailSync }}</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3">Quota d'envoi quotidien SMTP (Gmail)</span>
            </div>
            <div class="text-caption font-weight-bold" :class="dailyQuota.is_quota_exceeded ? 'red--text' : 'indigo--text text--darken-2'">
              {{ dailyQuota.daily_quota_used }} / {{ dailyQuota.daily_quota_max }} destinataires (24h glissantes)
            </div>
          </div>
          <div class="custom-progress-track my-2">
            <div
              class="custom-progress-fill"
              :class="dailyQuota.is_quota_exceeded ? 'bg-red' : (dailyQuota.daily_quota_used > 350 ? 'bg-orange' : 'bg-indigo')"
              :style="{ width: quotaPercentage + '%' }"
            ></div>
          </div>
          <div class="d-flex justify-space-between align-center mt-2 text-caption grey--text text--darken-1">
            <span>Reste : {{ dailyQuota.daily_quota_remaining }} destinataires aujourd'hui</span>
            <span v-if="dailyQuota.is_quota_exceeded" class="red--text font-weight-medium">⚠️ Quota dépassé ou bloqué — Réinitialisation dans 24h</span>
            <span v-else class="green--text font-weight-medium">✓ Quota disponible</span>
          </div>
        </div>

        <v-row class="mt-2">
          <!-- Card 1: Envoi Immédiat -->
          <v-col cols="12" md="6">
            <div class="bg-light-purple pa-4 rounded-lg fill-height d-flex flex-column justify-space-between border">
              <div>
                <div class="text-subtitle-1 font-weight-bold mb-1 d-flex align-center">
                  <v-icon class="mr-2" color="primary">{{ icons.mdiSendClock }}</v-icon>
                  Envoi Immédiat (Ponctuel)
                </div>
                <div class="text-caption text--secondary mb-3">
                  Déclenchez un envoi ponctuel des données du 1er janvier jusqu'au mois en cours.
                </div>

                <div class="font-weight-medium text-caption grey--text text--darken-2 mb-1">Sites à inclure dans ce rapport ponctuel :</div>
                <v-select
                  v-model="selectedSitesImmediate"
                  :items="filteredSitesImmediateList"
                  item-text="name"
                  item-value="id"
                  multiple
                  dense
                  outlined
                  hide-details
                  :placeholder="`0 site(s) sélectionné(s) / ${filteredSites.length}`"
                  class="mb-3 bg-white"
                >
                  <template v-slot:selection="{ index }">
                    <span v-if="index === 0" class="text-caption font-weight-bold primary--text">
                      {{ selectedSitesImmediate.length }} site(s) sélectionné(s) / {{ filteredSites.length }}
                    </span>
                  </template>
                  <template v-slot:prepend-item>
                    <div class="px-2 pt-2 pb-1">
                      <v-text-field
                        v-model="siteSearchImmediate"
                        placeholder="Rechercher un site..."
                        dense
                        outlined
                        hide-details
                        clearable
                        :prepend-inner-icon="icons.mdiMagnify"
                        @click.stop
                        @keydown.stop
                      />
                    </div>
                    <v-list-item
                      ripple
                      @click="toggleAllImmediateSites"
                    >
                      <v-list-item-action>
                        <v-icon :color="selectedSitesImmediate.length > 0 ? 'primary' : ''">
                          {{ selectedSitesImmediate.length === filteredSites.length ? icons.mdiCheckboxMarked : (selectedSitesImmediate.length > 0 ? icons.mdiMinusBox : icons.mdiCheckboxBlankOutline) }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">
                          {{ selectedSitesImmediate.length === filteredSites.length ? 'Tout désélectionner' : 'Tout sélectionner' }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-1"></v-divider>
                  </template>
                </v-select>

                <div class="font-weight-medium text-caption grey--text text--darken-2 mb-1">Mode d'envoi pour cette action :</div>
                <v-radio-group v-model="immediateSendType" row hide-details class="mt-0 mb-3" @change="onImmediateSendTypeChange">
                  <v-radio label="Envoi direct" value="direct" color="primary"></v-radio>
                  <v-radio label="Envoi en draft" value="draft" color="primary"></v-radio>
                </v-radio-group>


                <v-expand-transition>
                  <div v-if="immediateSendType === 'draft'" class="mb-3 pa-3 bg-white rounded border d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-icon small color="primary" class="mr-2">{{ icons.mdiEmailSync }}</v-icon>
                      <span class="text-caption grey--text text--darken-3 font-weight-medium">
                        Envoi Draft ponctuel vers : <strong>{{ config.immediate_draft_recipient_email || 'programmatique@reworldmedia.com' }}</strong>
                      </span>
                    </div>
                    <rmr-icon-button
                      :icon="icons.mdiPencil"
                      variant="edit"
                      size="sm"
                      title="Modifier l'email draft ponctuel"
                      @click="openEditDraftModal('immediate')"
                    />
                  </div>
                </v-expand-transition>
              </div>

              <div class="mt-4">
                <!-- Quota Alert Banner if exceeded -->
                <v-alert
                  v-if="dailyQuota.is_quota_exceeded"
                  type="error"
                  dense
                  outlined
                  class="mb-3 text-caption"
                >
                  <strong>Quota 24h atteint :</strong> Limite d'envoi Gmail ({{ dailyQuota.daily_quota_max }}/24h) atteinte ou bloquée. Déblocage automatique dans 24h.
                </v-alert>

                <div class="d-flex align-center">
                  <rmr-action-button
                    label="Aperçu du rapport"
                    variant="transparent"
                    :icon="icons.mdiEyeOutline"
                    :loading="loadingPreview"
                    :disabled="!selectedSitesImmediate || selectedSitesImmediate.length === 0"
                    class="flex-grow-1 mr-3"
                    @click="openPreviewModal"
                  />
                  <rmr-action-button
                    label="Envoyer maintenant"
                    variant="primary"
                    :icon="icons.mdiSend"
                    :loading="triggeringImmediate"
                    :disabled="dailyQuota.is_quota_exceeded || !selectedSitesImmediate || selectedSitesImmediate.length === 0"
                    class="flex-grow-1"
                    @click="openImmediateModal"
                  />
                </div>
              </div>
            </div>
          </v-col>


          <!-- Card 2: Envoi Programmé (DAG Hebdomadaire) -->
          <v-col cols="12" md="6">
            <div
              class="pa-4 rounded-lg fill-height d-flex flex-column justify-space-between transition-swing"
              :class="config.is_scheduled_active ? 'scheduled-card-active' : 'bg-light-purple border'"
            >
              <div>
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="text-subtitle-1 font-weight-bold d-flex align-center">
                    <v-icon class="mr-2" :color="config.is_scheduled_active ? 'purple darken-2' : 'purple darken-1'">{{ icons.mdiCalendarClock }}</v-icon>
                    Envoi Programmé (Chaque Lundi)
                  </div>
                  <rmr-status-chip
                    :label="config.is_scheduled_active ? 'PROGRAMMATION ACTIVE' : 'INACTIF'"
                    :variant="config.is_scheduled_active ? 'primary' : 'neutral'"
                    size="sm"
                  />
                </div>
                <div class="text-caption text--secondary mb-3">
                  Activation de la tâche automatique (DAG Airflow) exécutée chaque lundi.
                </div>

                <div class="font-weight-medium text-caption grey--text text--darken-2 mb-1">Sites à inclure dans les envois programmés :</div>
                <v-select
                  v-model="config.selected_site_ids"
                  :items="filteredSitesScheduledList"
                  item-text="name"
                  item-value="id"
                  multiple
                  dense
                  outlined
                  hide-details
                  :placeholder="`0 site(s) sélectionné(s) / ${filteredSites.length}`"
                  class="mb-3 bg-white"
                  @change="saveConfig"
                >
                  <template v-slot:selection="{ index }">
                    <span v-if="index === 0" class="text-caption font-weight-bold purple--text text--darken-2">
                      {{ (config.selected_site_ids || []).length }} site(s) sélectionné(s) / {{ filteredSites.length }}
                    </span>
                  </template>
                  <template v-slot:prepend-item>
                    <div class="px-2 pt-2 pb-1">
                      <v-text-field
                        v-model="siteSearchScheduled"
                        placeholder="Rechercher un site..."
                        dense
                        outlined
                        hide-details
                        clearable
                        :prepend-inner-icon="icons.mdiMagnify"
                        @click.stop
                        @keydown.stop
                      />
                    </div>
                    <v-list-item
                      ripple
                      @click="toggleAllScheduledSites"
                    >
                      <v-list-item-action>
                        <v-icon :color="(config.selected_site_ids || []).length > 0 ? 'purple darken-2' : ''">
                          {{ (config.selected_site_ids || []).length === filteredSites.length ? icons.mdiCheckboxMarked : ((config.selected_site_ids || []).length > 0 ? icons.mdiMinusBox : icons.mdiCheckboxBlankOutline) }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">
                          {{ (config.selected_site_ids || []).length === filteredSites.length ? 'Tout désélectionner' : 'Tout sélectionner' }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-1"></v-divider>
                  </template>
                </v-select>

                <div
                  class="d-flex align-center my-3 pa-3 rounded border transition-swing"
                  :class="config.is_scheduled_active ? 'bg-purple-purple-subtle border-purple-active' : 'bg-white'"
                >
                  <v-switch
                    v-model="config.is_scheduled_active"
                    color="purple darken-2"
                    hide-details
                    class="ma-0 pa-0"
                    :disabled="!config.selected_site_ids || config.selected_site_ids.length === 0"
                    @change="requestToggleScheduled"
                  ></v-switch>
                  <span class="text-body-2 font-weight-bold ml-3" :class="config.is_scheduled_active ? 'purple--text text--darken-3' : 'grey--text text--darken-2'">
                    {{ config.is_scheduled_active ? 'Programmation automatique activée' : 'Programmation automatique désactivée' }}
                  </span>
                </div>

                <div class="font-weight-medium text-caption grey--text text--darken-2 mb-1">Mode d'envoi récurrent :</div>
                <v-radio-group v-model="config.scheduled_send_type" row hide-details class="mt-0 mb-2" @change="saveConfig">
                  <v-radio label="Envoi direct" value="direct" color="purple darken-2"></v-radio>
                  <v-radio label="Envoi en draft" value="draft" color="purple darken-2"></v-radio>
                </v-radio-group>

                <v-expand-transition>
                  <div v-if="config.scheduled_send_type === 'draft'" class="mb-2 pa-2 bg-white rounded border d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-icon x-small color="purple darken-1" class="mr-2">{{ icons.mdiEmailSync }}</v-icon>
                      <span class="text-caption grey--text text--darken-3">
                        Envoi Draft planifié vers : <strong>{{ config.scheduled_draft_recipient_email || 'programmatique@reworldmedia.com' }}</strong>
                      </span>
                    </div>
                    <rmr-icon-button
                      :icon="icons.mdiPencil"
                      variant="edit"
                      size="sm"
                      title="Modifier l'email draft planifié"
                      @click="openEditDraftModal('scheduled')"
                    />
                  </div>
                </v-expand-transition>
              </div>

              <div class="text-caption text--secondary mt-3 font-italic">
                Exécution hebdomadaire récurrente tous les lundis matin avec données YTD du 1er janvier.
              </div>
            </div>
          </v-col>
        </v-row>

      </v-card-text>

    </v-card>

    <!-- Historique & Rattrapage Mensuel Card -->
    <v-card elevation="2" class="mb-6 rounded-xl rmr-card rmr-card-amber">
      <v-card-title class="d-flex justify-space-between align-center py-4 px-6 border-bottom">
        <div class="d-flex align-center">
          <v-icon class="mr-2" color="warning">{{ icons.mdiHistory }}</v-icon>
          <span class="text-subtitle-1 font-weight-bold text-uppercase grey--text text--darken-2">Historique & Rattrapage Mensuel</span>
        </div>
        <div class="d-flex align-center">
          <rmr-action-button
            label="Lancer un rattrapage"
            variant="primary"
            size="sm"
            :icon="icons.mdiHistory"
            class="mr-3"
            @click="openStandaloneRattrapageModal"
          />
          <rmr-action-button
            :label="showHistory ? 'Masquer' : 'Afficher l\'historique'"
            variant="ghost"
            size="sm"
            :icon="showHistory ? icons.mdiChevronUp : icons.mdiChevronDown"
            @click="showHistory = !showHistory"
          />
        </div>
      </v-card-title>
      <v-expand-transition>
        <div v-show="showHistory">
          <v-card-text class="pa-4">
            <v-data-table
              :headers="historyHeaders"
              :items="historyList"
              :loading="loadingHistory"
              dense
              class="elevation-0"
              no-data-text="Aucune action enregistrée"
            >
              <template v-slot:item.sent_at="{ item }">
                <span class="font-weight-medium">{{ formatDateTime(item.sent_at) }}</span>
              </template>

              <template v-slot:item.action_type="{ item }">
                <rmr-soft-badge
                  :label="getActionTypeLabel(item.action_type)"
                  :color="colors.indigo"
                  :background="colors.indigoBg"
                />
              </template>

              <template v-slot:item.send_type="{ item }">
                <rmr-soft-badge
                  :label="item.send_type === 'direct' ? 'Direct' : 'Draft'"
                  :color="colors.indigo"
                  :background="colors.indigoBg"
                />
              </template>

              <template v-slot:item.sites="{ item }">
                <div v-if="item.selected_site_ids && item.selected_site_ids.length > 0">
                  <v-chip
                    small
                    class="font-weight-medium text-caption"
                    color="primary"
                    outlined
                    style="cursor: pointer"
                    @click.stop="openHistorySitesModal(item)"
                  >
                    <v-icon left small>{{ icons.mdiMapMarkerPath }}</v-icon>
                    {{ item.selected_site_ids.length }} site(s)
                  </v-chip>
                </div>
                <span v-else class="text-caption grey--text italic">Tous les sites</span>
              </template>

              <template v-slot:item.target_month="{ item }">
                <span class="font-weight-bold">{{ item.target_month || formatDateMonth(item.target_date) }}</span>
              </template>

              <template v-slot:item.status="{ item }">
                <div class="d-flex align-center">
                  <div
                    v-if="item.status === 'in_progress' || item.status === 'pending'"
                    class="d-flex align-center px-3 py-1 rounded-pill status-in-progress-pill"
                  >
                    <v-progress-circular
                      indeterminate
                      size="14"
                      width="2"
                      color="warning darken-3"
                      class="mr-2"
                    ></v-progress-circular>
                    <span class="text-caption font-weight-bold warning--text text--darken-4">En cours...</span>
                  </div>
                  <rmr-status-chip
                    v-else-if="item.status === 'success' || item.status === 'sent' || item.status === 'draft_created'"
                    label="Succès"
                    variant="success"
                    size="sm"
                  />
                  <rmr-status-chip
                    v-else
                    label="Échec"
                    variant="error"
                    size="sm"
                  />
                  <v-btn
                    v-if="item.error_message && item.status !== 'in_progress'"
                    icon
                    x-small
                    color="error"
                    class="ml-1"
                    title="Voir les détails de l'erreur"
                    @click.stop="openErrorDetailsModal(item)"
                  >
                    <v-icon small>{{ icons.mdiAlertCircle }}</v-icon>
                  </v-btn>
                </div>
              </template>

              <template v-slot:item.actions="{ item }">
                <rmr-action-button
                  label="Rattraper le mois"
                  variant="ghost"
                  size="xs"
                  :icon="icons.mdiHistory"
                  :disabled="item.status === 'in_progress' || item.status === 'pending'"
                  :loading="executingRattrapage && selectedRattrapageItem && selectedRattrapageItem.id === item.id"
                  @click="openRattrapageModal(item)"
                />
              </template>

            </v-data-table>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Historique des Synchronisations GSS Card -->
    <v-card elevation="2" class="mb-6 rounded-xl rmr-card rmr-card-indigo">
      <v-card-title class="d-flex justify-space-between align-center py-4 px-6 border-bottom">
        <div class="d-flex align-center">
          <v-icon class="mr-2" color="indigo">{{ icons.mdiGoogleSpreadsheet }}</v-icon>
          <span class="text-subtitle-1 font-weight-bold text-uppercase grey--text text--darken-2">Historique des Synchronisations GSS</span>
        </div>
        <div class="d-flex align-center">
          <rmr-action-button
            :label="showGssSyncHistory ? 'Masquer' : 'Afficher l\'historique'"
            variant="ghost"
            size="sm"
            :icon="showGssSyncHistory ? icons.mdiChevronUp : icons.mdiChevronDown"
            @click="showGssSyncHistory = !showGssSyncHistory"
          />
        </div>
      </v-card-title>
      <v-expand-transition>
        <div v-show="showGssSyncHistory">
          <v-card-text class="pa-4">
            <v-data-table
              :headers="gssSyncHistoryHeaders"
              :items="gssSyncHistoryList"
              :loading="loadingGssSyncHistory"
              dense
              class="elevation-0"
              no-data-text="Aucune synchronisation GSS enregistrée"
            >
              <template v-slot:item.created_at="{ item }">
                <span class="font-weight-medium">{{ formatDateTime(item.created_at) }}</span>
              </template>

              <template v-slot:item.triggered_by="{ item }">
                <v-chip
                  small
                  class="font-weight-bold"
                  :style="isSystemTriggered(item) ? 'background-color: #f1f5f9 !important; border: 1px solid #cbd5e1;' : 'background-color: #e0e7ff !important; border: 1px solid #c7d2fe;'"
                >
                  <v-icon left x-small :style="isSystemTriggered(item) ? 'color: #475569 !important;' : 'color: #4338ca !important;'">
                    {{ isSystemTriggered(item) ? icons.mdiRobot : icons.mdiAccount }}
                  </v-icon>
                  <span :style="isSystemTriggered(item) ? 'color: #1e293b !important; font-weight: 600;' : 'color: #1e1b4b !important; font-weight: 600;'">
                    {{ item.user_name || item.user_email || item.triggered_by || 'Manuel' }}
                  </span>
                </v-chip>
              </template>


              <template v-slot:item.spreadsheet_url="{ item }">
                <div v-if="item.spreadsheet_url" class="d-flex align-center">
                  <v-icon left x-small color="primary">{{ icons.mdiLinkVariant }}</v-icon>
                  <a :href="item.spreadsheet_url" target="_blank" class="text-caption text-truncate hover-underline primary--text font-weight-medium" style="max-width: 280px;">
                    {{ item.spreadsheet_url }}
                  </a>
                </div>
                <span v-else class="text-caption grey--text italic">URL par défaut</span>
              </template>

              <template v-slot:item.target_tab="{ item }">
                <v-chip small color="#2c3e50" dark class="font-weight-bold px-3">
                  {{ item.target_tab || 'Tous les onglets (Globale)' }}
                </v-chip>
              </template>


              <template v-slot:item.status="{ item }">
                <div class="d-flex align-center">
                  <div
                    v-if="item.status === 'in_progress'"
                    class="d-flex align-center px-3 py-1 rounded-pill status-in-progress-pill"
                  >
                    <v-progress-circular
                      indeterminate
                      size="14"
                      width="2"
                      color="warning darken-3"
                      class="mr-2"
                    ></v-progress-circular>
                    <span class="text-caption font-weight-bold warning--text text--darken-4">En cours...</span>
                  </div>
                  <rmr-status-chip
                    v-else-if="item.status === 'success'"
                    label="Succès"
                    variant="success"
                    size="sm"
                  />
                  <rmr-status-chip
                    v-else
                    label="Échec"
                    variant="error"
                    size="sm"
                  />
                  <v-btn
                    v-if="item.error_message && item.status !== 'in_progress'"
                    icon
                    x-small
                    color="error"
                    class="ml-1"
                    title="Voir les détails de l'erreur"
                    @click.stop="openErrorDetailsModal(item)"
                  >
                    <v-icon small>{{ icons.mdiAlertCircle }}</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>



    <!-- Stacked Cards: Acteurs & Périmètres -->
    <v-row class="mb-6">
      <!-- Table 1: Acteurs -->
      <v-col cols="12" class="mb-6">
        <v-card elevation="2" class="rounded-xl rmr-card rmr-card-blue">
          <v-card-title class="d-flex justify-space-between align-center py-4 px-6 border-bottom">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="blue darken-2">{{ icons.mdiAccountGroup }}</v-icon>
              <span class="text-subtitle-1 font-weight-bold text-uppercase grey--text text--darken-2">Acteurs</span>
              <v-chip x-small color="blue" text-color="white" class="ml-2 font-weight-bold">{{ actors.length }}</v-chip>
            </div>
            <div class="d-flex align-center">
              <rmr-action-button
                label="Ajouter"
                variant="primary"
                size="sm"
                :icon="icons.mdiPlus"
                class="mr-3"
                @click="openActorDialog()"
              />
              <rmr-action-button
                :label="showActors ? 'Masquer' : 'Afficher'"
                variant="ghost"
                size="sm"
                :icon="showActors ? icons.mdiChevronUp : icons.mdiChevronDown"
                @click="showActors = !showActors"
              />
            </div>
          </v-card-title>

          <v-expand-transition>
            <div v-show="showActors">
              <v-card-text class="pa-4">
                <rmr-text-field
                  v-model="searchActor"
                  placeholder="Rechercher un acteur..."
                  class="mb-4"
                  :prepend-inner-icon="icons.mdiMagnify"
                />

                <v-data-table
                  :headers="actorHeaders"
                  :items="actors"
                  :search="searchActor"
                  :loading="loadingActors"
                  dense
                  class="elevation-0"
                >
                  <template v-slot:item.name="{ item }">
                    <div
                      class="d-flex align-center cursor-pointer primary--text hover-underline"
                      :class="{ 'grey--text text--disabled': item.is_active === false }"
                      @click="openActorDetails(item)"
                    >
                      <span class="text-body-2 font-weight-medium text-no-wrap">{{ item.name }}</span>
                    </div>
                  </template>
                  <template v-slot:item.email="{ item }">
                    <span class="text-caption grey--text text--darken-2 text-no-wrap" :class="{ 'grey--text text--disabled': item.is_active === false }">{{ item.email }}</span>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex align-center justify-end text-no-wrap">
                      <rmr-icon-button
                        :icon="item.is_active !== false ? icons.mdiCancel : icons.mdiCheckCircle"
                        :variant="item.is_active !== false ? 'neutral' : 'success'"
                        :tooltip="item.is_active !== false ? 'Désactiver l\'acteur' : 'Activer l\'acteur'"
                        class="mr-1"
                        @click="toggleActorActive(item)"
                      />
                      <rmr-icon-button
                        :icon="icons.mdiPencil"
                        variant="edit"
                        tooltip="Modifier l'acteur"
                        class="mr-1"
                        @click="openActorDialog(item)"
                      />
                      <rmr-icon-button
                        :icon="icons.mdiDelete"
                        variant="delete"
                        tooltip="Supprimer l'acteur"
                        @click="deleteActor(item.id)"
                      />
                    </div>
                  </template>

                </v-data-table>
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>

      <!-- Table 2: Périmètres -->
      <v-col cols="12">
        <v-card elevation="2" class="rounded-xl rmr-card rmr-card-indigo">
          <v-card-title class="d-flex justify-space-between align-center py-4 px-6 border-bottom">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="indigo darken-2">{{ icons.mdiMapMarkerPath }}</v-icon>
              <span class="text-subtitle-1 font-weight-bold text-uppercase grey--text text--darken-2">Périmètres</span>
              <v-chip x-small color="indigo" text-color="white" class="ml-2 font-weight-bold">{{ perimeters.length }}</v-chip>
            </div>
            <div class="d-flex align-center">
              <rmr-action-button
                label="Ajouter"
                variant="primary"
                size="sm"
                :icon="icons.mdiPlus"
                class="mr-3"
                @click="openPerimeterDialog()"
              />
              <rmr-action-button
                :label="showPerimeters ? 'Masquer' : 'Afficher'"
                variant="ghost"
                size="sm"
                :icon="showPerimeters ? icons.mdiChevronUp : icons.mdiChevronDown"
                @click="showPerimeters = !showPerimeters"
              />
            </div>
          </v-card-title>

          <v-expand-transition>
            <div v-show="showPerimeters">
              <v-card-text class="pa-4">
                <rmr-text-field
                  v-model="searchPerimeter"
                  placeholder="Rechercher un périmètre..."
                  class="mb-4"
                  :prepend-inner-icon="icons.mdiMagnify"
                />

                <v-data-table
                  :headers="perimeterHeaders"
                  :items="perimeters"
                  :search="searchPerimeter"
                  :loading="loadingPerimeters"
                  dense
                  class="elevation-0"
                >
                  <template v-slot:item.name="{ item }">
                    <div class="font-weight-bold text-subtitle-2 primary--text text-no-wrap" :class="{ 'grey--text text--disabled': item.is_active === false }">
                      {{ item.name }}
                    </div>
                  </template>

                  <!-- Elegant & Readable Acteurs Chips (Active Actors Only) -->
                  <template v-slot:item.actors="{ item }">
                    <div class="d-flex flex-wrap align-center py-2 gap-1">
                      <template v-if="getActiveActors(item) && getActiveActors(item).length">
                        <!-- Display first 3 actor chips -->
                        <v-chip
                          v-for="act in getActiveActors(item).slice(0, 3)"
                          :key="act.id"
                          small
                          class="ma-1 actor-chip custom-actor-chip"
                          @click="openActorDetails(act)"
                        >
                          <span class="white--text font-weight-bold">{{ act.name }}</span>
                        </v-chip>


                        <!-- Expandable badge for remaining actors -->
                        <v-menu v-if="getActiveActors(item).length > 3" open-on-hover top offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              small
                              class="ma-1 font-weight-bold custom-more-chip"
                              v-bind="attrs"
                              v-on="on"
                            >
                              +{{ getActiveActors(item).length - 3 }} autres
                            </v-chip>
                          </template>

                          <v-list class="pa-1 actor-menu-list">
                            <template v-for="(act, idx) in getActiveActors(item).slice(3)">
                              <v-list-item
                                :key="act.id"
                                class="actor-menu-item py-2 px-3"
                                @click="openActorDetails(act)"
                              >
                                <v-list-item-content class="pa-0">
                                  <v-list-item-title class="text-body-2 font-weight-bold primary--text mb-1">{{ act.name }}</v-list-item-title>
                                  <v-list-item-subtitle class="text-caption grey--text text--darken-1">{{ act.email }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                              <v-divider v-if="idx < getActiveActors(item).slice(3).length - 1" :key="'div-' + act.id" class="my-1"></v-divider>
                            </template>
                          </v-list>


                        </v-menu>
                      </template>

                      <span v-else class="text-caption grey--text italic">
                        Aucun acteur actif associé
                      </span>
                    </div>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex align-center justify-end text-no-wrap">
                      <rmr-icon-button
                        :icon="item.is_active !== false ? icons.mdiCancel : icons.mdiCheckCircle"
                        :variant="item.is_active !== false ? 'neutral' : 'success'"
                        :tooltip="item.is_active !== false ? 'Désactiver le périmètre' : 'Activer le périmètre'"
                        class="mr-1"
                        @click="togglePerimeterActive(item)"
                      />
                      <rmr-icon-button
                        :icon="icons.mdiPencil"
                        variant="edit"
                        tooltip="Modifier le périmètre"
                        class="mr-1"
                        @click="openPerimeterDialog(item)"
                      />
                      <rmr-icon-button
                        :icon="icons.mdiDelete"
                        variant="delete"
                        tooltip="Supprimer le périmètre"
                        @click="deletePerimeter(item.id)"
                      />
                    </div>
                  </template>

                </v-data-table>
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog Acteur (Création/Édition) -->
    <rmr-modal
      v-model="actorDialog"
      variant="form"
      :icon="icons.mdiAccountGroup"
      eyebrow="Gestion des acteurs"
      :title="editedActor.id ? 'Modifier un acteur' : 'Ajouter un acteur'"
      max-width="500"
      @close="actorDialog = false"
    >
      <template #body>
        <div class="pa-6">
          <rmr-text-field
            v-model="editedActor.name"
            label="Nom complet"
            class="mb-4"
          />
          <rmr-text-field
            v-model="editedActor.email"
            label="Adresse email"
            type="email"
          />
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-end gap-2 pa-4 w-100">
          <rmr-action-button label="Annuler" variant="ghost" @click="actorDialog = false" />
          <rmr-action-button label="Enregistrer" variant="primary" :loading="savingActor" @click="saveActor" />
        </div>
      </template>
    </rmr-modal>

    <!-- Dialog Fiche Acteur (Détails au clic sur un acteur) -->
    <rmr-modal
      v-model="actorDetailsDialog"
      variant="form"
      :icon="icons.mdiAccountGroup"
      eyebrow="Fiche Acteur"
      :title="selectedActorDetail ? selectedActorDetail.name : 'Détails de l\'acteur'"
      :subtitle="selectedActorDetail ? selectedActorDetail.email : ''"
      max-width="500"
      @close="actorDetailsDialog = false"
    >
      <template #body>
        <div class="pa-6" v-if="selectedActorDetail">
          <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-2">
            Périmètres dans lesquels cet acteur est inscrit :
          </div>
          <div class="d-flex flex-wrap gap-1 mb-4">
            <rmr-soft-badge
              v-for="p in getActorPerimeters(selectedActorDetail.id)"
              :key="p.id"
              variant="primary"
              class="ma-1 font-weight-medium"
            >
              <v-icon left small color="primary">{{ icons.mdiMapMarkerPath }}</v-icon>
              {{ p.name }}
            </rmr-soft-badge>

            <span
              v-if="!getActorPerimeters(selectedActorDetail.id).length"
              class="text-caption grey--text italic"
            >
              Aucun périmètre rattaché.
            </span>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-end gap-2 pa-4 w-100">
          <rmr-action-button
            label="Modifier cet acteur"
            variant="ghost"
            :icon="icons.mdiPencil"
            @click="actorDetailsDialog = false; openActorDialog(selectedActorDetail)"
          />
          <rmr-action-button label="Fermer" variant="primary" @click="actorDetailsDialog = false" />
        </div>
      </template>
    </rmr-modal>

    <!-- Dialog Périmètre -->
    <rmr-modal
      v-model="perimeterDialog"
      variant="form"
      :icon="icons.mdiMapMarkerPath"
      eyebrow="Gestion des périmètres"
      :title="editedPerimeter.id ? 'Modifier un périmètre' : 'Ajouter un périmètre'"
      max-width="600"
      @close="perimeterDialog = false"
    >
      <template #body>
        <div class="pa-6">
          <div class="text-caption font-weight-medium grey--text text--darken-2 mb-1">
            Sélectionner un ou plusieurs sites RMR (ou saisir un nom personnalisé) :
          </div>
          <v-combobox
            v-model="editedPerimeter.sites_selected"
            :items="availableSites"
            item-text="name"
            item-value="name"
            label="Nom du périmètre / Sites associés"
            multiple
            chips
            deletable-chips
            outlined
            dense
            placeholder="Sélectionnez des sites dans la liste..."
            class="mb-4"
            @change="onSitesSelectionChange"
          >
            <template v-slot:selection="{ item }">
              <v-chip small class="ma-1" color="primary lighten-4" text-color="primary darken-3">
                <span>{{ typeof item === 'object' ? item.name : item }}</span>
              </v-chip>
            </template>
          </v-combobox>

          <rmr-text-field
            v-model="editedPerimeter.name"
            label="Nom du périmètre (final)"
            hint="Ce nom sera affiché pour le périmètre de rapport"
            persistent-hint
            class="mb-4"
          />

          <v-select
            v-model="editedPerimeter.actor_ids"
            :items="actors"
            item-text="name"
            item-value="id"
            label="Envoyer à (Acteurs associés)"
            multiple
            chips
            deletable-chips
            outlined
            dense
          >
            <template v-slot:selection="{ item }">
              <v-chip small class="ma-1" color="indigo lighten-5" text-color="indigo darken-4">
                <v-avatar left color="indigo lighten-3" size="18">
                  <span class="indigo--text text-caption font-weight-bold">{{ getInitial(item.name) }}</span>
                </v-avatar>
                {{ item.name }}
              </v-chip>
            </template>
          </v-select>
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-end gap-2 pa-4 w-100">
          <rmr-action-button label="Annuler" variant="ghost" @click="perimeterDialog = false" />
          <rmr-action-button label="Enregistrer" variant="primary" :loading="savingPerimeter" @click="savePerimeter" />
        </div>
      </template>
    </rmr-modal>

    <!-- Confirm Dialog Delete Actor -->
    <rmr-confirm-dialog
      v-model="confirmDeleteActorModal"
      title="Supprimer l'acteur"
      message="Voulez-vous vraiment supprimer cet acteur ?"
      confirm-label="Supprimer"
      cancel-label="Annuler"
      danger
      @confirm="executeDeleteActor"
    />

    <!-- Confirm Dialog Delete Perimeter -->
    <rmr-confirm-dialog
      v-model="confirmDeletePerimeterModal"
      title="Supprimer le périmètre"
      message="Voulez-vous vraiment supprimer ce périmètre ?"
      confirm-label="Supprimer"
      cancel-label="Annuler"
      danger
      @confirm="executeDeletePerimeter"
    />

    <!-- Confirm Dialog Synchronisation GSS -->
    <rmr-confirm-dialog
      v-model="confirmSyncModal"
      title="Confirmation de la synchronisation"
      confirm-label="Synchroniser"
      cancel-label="Annuler"
      @confirm="executeConfirmSync"
    >
      <template #message>
        Êtes-vous sûr de vouloir synchroniser les données depuis le fichier Google Spreadsheet ? Cette opération mettra à jour l'ensemble des données dans la base de données.
      </template>
    </rmr-confirm-dialog>

    <!-- Modal: Aperçu du Rapport -->
    <rmr-modal
      v-model="previewModal"
      variant="gradient"
      :icon="icons.mdiEyeOutline"
      eyebrow="Rapport Mail Auto"
      title="Aperçu du rapport hebdomadaire"
      :subtitle="previewData ? `Données arrêtées au ${previewData.end_date_formatted} (S${String(previewData.week_number).padStart(2, '0')})` : ''"
      max-width="1200"
      scrollable
      @close="previewModal = false"
    >
      <template #body>
        <div class="pa-4 d-flex flex-column" style="min-height: 600px; max-height: 80vh; overflow-y: auto;">
          <!-- 1. Multi-site Selector if more than 1 report -->
          <div v-if="previewData && previewData.previews && previewData.previews.length > 1" class="mb-4">
            <div class="text-caption font-weight-bold grey--text text--darken-3 text-uppercase mb-2">
              Sélectionnez un site pour afficher son rapport ({{ previewData.previews.length }} sites sélectionnés) :
            </div>
            <div class="d-flex flex-wrap gap-2">
              <v-btn
                v-for="(p, idx) in previewData.previews"
                :key="p.site_id || idx"
                small
                depressed
                :color="selectedPreviewIndex === idx ? 'primary' : 'grey lighten-3'"
                :class="selectedPreviewIndex === idx ? 'white--text font-weight-bold' : 'grey--text text--darken-3 font-weight-medium'"
                class="rounded-pill text-capitalize"
                @click="selectedPreviewIndex = idx"
              >
                {{ p.site_name }}
              </v-btn>
            </div>
          </div>

          <!-- 2. Metadata Banner -->
          <div v-if="currentPreviewReport" class="mb-3 pa-3 rounded-lg border bg-light-purple d-flex flex-column gap-2 text-caption">
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div>
                <strong class="primary--text">Sujet :</strong>
                <span class="font-weight-medium ml-1">{{ currentPreviewReport.subject }}</span>
              </div>
              <div>
                <strong class="primary--text">Mode :</strong>
                <span class="ml-1">{{ currentPreviewReport.mode_label }}</span>
              </div>
            </div>
            <div class="d-flex align-center flex-wrap gap-1">
              <strong class="primary--text mr-1">Destinataires prévus :</strong>
              <template v-if="currentPreviewReport.recipients && currentPreviewReport.recipients.length > 0">
                <v-chip
                  v-for="(email, eIdx) in currentPreviewReport.recipients"
                  :key="eIdx"
                  x-small
                  color="indigo lighten-5"
                  text-color="indigo darken-3"
                  class="font-weight-medium"
                >
                  {{ email }}
                </v-chip>
              </template>
              <span v-else class="grey--text font-italic">Aucun destinataire actif associé à ce site</span>
            </div>
          </div>

          <!-- 3. Isolated HTML iframe preview -->
          <div v-if="currentPreviewReport" class="flex-grow-1" style="min-height: 500px;">
            <iframe
              :srcdoc="currentPreviewReport.html"
              sandbox="allow-same-origin"
              style="width: 100%; height: clamp(480px, 62vh, 700px); border: 1px solid #cbd5e1; border-radius: 8px; background-color: #f1f5f9;"
              frameborder="0"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <div class="d-flex justify-space-between align-center pa-4 w-100 border-top bg-white">
          <div class="text-caption grey--text text--darken-2">
            Aperçu conforme au rendu final reçu par email.
          </div>
          <div class="d-flex gap-2">
            <rmr-action-button label="Fermer" variant="ghost" @click="previewModal = false" />
            <rmr-action-button
              label="Confirmer et envoyer"
              variant="primary"
              :icon="icons.mdiSend"
              :loading="triggeringImmediate"
              :disabled="dailyQuota.is_quota_exceeded"
              @click="executeImmediateSend"
            />
          </div>
        </div>
      </template>
    </rmr-modal>

    <!-- Modal 1: Confirmation Envoi Immédiat -->
    <rmr-confirm-dialog
      v-model="confirmImmediateModal"
      title="Confirmation de l'envoi immédiat"
      confirm-label="Confirmer et envoyer"
      cancel-label="Annuler"
      @confirm="executeImmediateSend"
    >
      <template #message>
        Vous allez lancer un envoi immédiat pour <strong>{{ selectedSitesImmediate.length }} site(s) sélectionné(s)</strong> en mode <strong>{{ immediateSendType === 'direct' ? 'Direct (Destinataires périmètres)' : `Draft (${config.immediate_draft_recipient_email || 'programmatique@reworldmedia.com'})` }}</strong>.
      </template>
    </rmr-confirm-dialog>

    <!-- Modal 2: Confirmation Toggle Envoi Programmé -->
    <rmr-confirm-dialog
      v-model="confirmToggleModal"
      title="Modification de la planification"
      :message="`Voulez-vous vraiment ${pendingScheduledState ? 'ACTIVER' : 'DÉSACTIVER'} l'envoi automatique hebdomadaire du rapport ?`"
      confirm-label="Confirmer"
      cancel-label="Annuler"
      @confirm="executeScheduledToggle"
      @cancel="cancelScheduledToggle"
    />

    <!-- Modal 3: Confirmation Rattrapage Mensuel -->
    <rmr-confirm-dialog
      v-model="confirmRattrapageModal"
      title="Confirmation du rattrapage mensuel"
      :message="selectedRattrapageItem ? `Vous allez lancer un rattrapage de rapport pour le mois de ${selectedRattrapageItem.target_month || formatDateMonth(selectedRattrapageItem.target_date)}.` : ''"
      confirm-label="Lancer le rattrapage"
      cancel-label="Annuler"
      @confirm="executeRattrapage"
    />

    <!-- Modal 4: Détails de l'erreur -->
    <rmr-modal
      v-model="errorDetailsModal"
      variant="gradient"
      :icon="icons.mdiAlertCircle"
      eyebrow="Rapport Mail Auto"
      title="Détails de l'erreur"
      subtitle="Information de débogage système"
      max-width="560"
      @close="errorDetailsModal = false"
    >
      <template #body>
        <div class="pa-6">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="text-caption font-weight-bold grey--text text--darken-3 text-uppercase">Message d'erreur système</span>
            <rmr-action-button
              label="Copier le message"
              variant="ghost"
              size="sm"
              :icon="icons.mdiContentCopy"
              @click="copyErrorText"
            />
          </div>

          <div
            class="pa-4 rounded-xl font-mono text-body-2 text-break"
            style="background-color: #0f172a !important; color: #f8fafc !important; border: 1px solid #1e293b; max-height: 260px; overflow-y: auto; line-height: 1.6;"
          >
            {{ selectedErrorItem ? selectedErrorItem.error_message : '' }}
          </div>
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-end pa-4 w-100">
          <rmr-action-button label="Fermer" variant="primary" @click="errorDetailsModal = false" />
        </div>
      </template>
    </rmr-modal>

    <!-- Modal 5: Modification Destinataire Draft Email -->
    <rmr-modal
      v-model="editDraftModal"
      variant="form"
      :icon="icons.mdiPencil"
      eyebrow="Rapport Mail Auto"
      :title="editDraftType === 'immediate' ? 'Destinataire Draft (Envoi Immédiat)' : 'Destinataire Draft (Envoi Programmé)'"
      subtitle="Adresse email utilisée pour recevoir les drafts"
      max-width="500"
      @close="editDraftModal = false"
    >
      <template #body>
        <div class="pa-6">
          <div class="text-caption grey--text text--darken-2 mb-3">
            Saisissez l'adresse email destinataire pour les envois en mode Draft
            <strong>{{ editDraftType === 'immediate' ? 'ponctuel (immédiat)' : 'automatiques récurrents (chaque lundi)' }}</strong>.
          </div>
          <rmr-text-field
            v-model="editDraftEmail"
            label="Adresse Email Destinataire Draft"
            placeholder="programmatique@reworldmedia.com"
            type="email"
            :icon="icons.mdiEmailSync"
          />
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-end gap-2 pa-4 w-100 border-top">
          <rmr-action-button label="Annuler" variant="ghost" @click="editDraftModal = false" />
          <rmr-action-button
            label="Enregistrer l'adresse"
            variant="primary"
            :loading="savingDraftEmail"
            @click="saveDraftRecipientEmail"
          />
        </div>
      </template>
    </rmr-modal>

    <!-- Modal 6: Relancer un Rattrapage Indépendant -->
    <rmr-modal
      v-model="standaloneRattrapageModal"
      variant="form"
      :icon="icons.mdiHistory"
      eyebrow="Rapport Mail Auto"
      title="Relancer un rattrapage de rapport"
      subtitle="Sélectionnez le mois à rattraper et le mode d'envoi"
      max-width="540"
      @close="standaloneRattrapageModal = false"
    >
      <template #body>
        <div class="pa-6">
          <div class="text-caption font-weight-medium grey--text text--darken-2 mb-2">
            Mois cible du rattrapage :
          </div>
          <v-select
            v-model="standaloneRattrapageMonth"
            :items="availableRattrapageMonths"
            item-text="text"
            item-value="value"
            label="Sélectionnez un mois"
            outlined
            dense
            class="mb-4"
          />

          <div class="font-weight-medium text-caption grey--text text--darken-2 mb-1">
            Mode d'envoi pour ce rattrapage :
          </div>
          <v-radio-group v-model="standaloneRattrapageSendType" row hide-details class="mt-0 mb-3">
            <v-radio label="Envoi direct" value="direct" color="primary"></v-radio>
            <v-radio label="Envoi en draft" value="draft" color="primary"></v-radio>
          </v-radio-group>

          <v-expand-transition>
            <div v-if="standaloneRattrapageSendType === 'draft'" class="mt-3 pa-3 bg-light-purple rounded border d-flex align-center">
              <v-icon small color="primary" class="mr-2">{{ icons.mdiEmailSync }}</v-icon>
              <span class="text-caption grey--text text--darken-3 font-weight-medium">
                Envoi Draft ponctuel vers : <strong>{{ config.immediate_draft_recipient_email || 'programmatique@reworldmedia.com' }}</strong>
              </span>
            </div>
          </v-expand-transition>
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-end gap-2 pa-4 w-100 border-top">
          <rmr-action-button label="Annuler" variant="ghost" @click="standaloneRattrapageModal = false" />
          <rmr-action-button
            label="Lancer le rattrapage"
            variant="primary"
            :icon="icons.mdiHistory"
            :loading="executingStandaloneRattrapage"
            @click="executeStandaloneRattrapage"
          />
        </div>
      </template>
    </rmr-modal>

    <!-- Modal liste des sites inclus dans un envoi de l'historique -->
    <rmr-modal
      v-model="historySitesModal"
      :icon="icons.mdiMapMarkerPath"
      title="Sites inclus dans cet envoi"
      max-width="500px"
    >
      <template #body>
        <div class="pa-4">
          <div class="text-subtitle-2 font-weight-bold mb-3 d-flex align-center">
            <v-icon left small color="primary">{{ icons.mdiMapMarkerPath }}</v-icon>
            {{ selectedHistorySites.length }} site(s) sélectionné(s) :
          </div>
          <div class="d-flex flex-wrap gap-2 pa-2 border rounded overflow-y-auto" style="max-height: 250px">
            <v-chip
              v-for="site in selectedHistorySites"
              :key="site.id"
              class="ma-1 font-weight-medium"
              color="primary"
              outlined
              small
            >
              <v-icon left small color="primary">{{ icons.mdiCheckCircle }}</v-icon>
              {{ site.name }}
            </v-chip>
          </div>
        </div>
      </template>

      <template v-slot:footer>
        <div class="d-flex justify-end pa-3 border-top bg-light">
          <rmr-action-button
            label="Fermer"
            variant="ghost"
            size="sm"
            @click="historySitesModal = false"
          />
        </div>
      </template>
    </rmr-modal>

    <!-- Snackbar global -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top timeout="4500">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Fermer</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { defineComponent } from "@vue/composition-api";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import { useActorsPerimeter } from "./useActorsPerimeter";

export default defineComponent({
  name: "ActorsPerimeter",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    RmrPageHero,
    RmrActionButton,
    RmrIconButton,
    RmrStatusChip,
    RmrSoftBadge,
    RmrModal,
    RmrConfirmDialog,
    RmrTextField,
    RmrSelect,
  },
  setup() {
    return useActorsPerimeter();
  },
});
</script>

<style scoped>
.auto-email-report-page {
  padding: 8px 0;
}

.badge-icon-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.bg-indigo-light {
  background-color: #e0e7ff;
}
.rmr-card {
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04) !important;
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.rmr-card-purple {
  border-top: 4px solid #6366f1 !important;
}
.rmr-card-blue {
  border-top: 4px solid #0284c7 !important;
}
.rmr-card-indigo {
  border-top: 4px solid #4338ca !important;
}
.rmr-card-amber {
  border-top: 4px solid #f59e0b !important;
}

.bg-light-purple {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}
.custom-progress-track {
  width: 100%;
  height: 10px;
  background-color: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}
.custom-progress-fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.4s ease-in-out;
}
.bg-indigo {
  background-color: #4f46e5 !important;
}
.bg-orange {
  background-color: #f97316 !important;
}
.bg-red {
  background-color: #ef4444 !important;
}
.scheduled-card-active {
  background-color: #ffffff !important;
  border: 2px solid #7c3aed !important;
  box-shadow: 0 0 20px rgba(124, 58, 237, 0.22), 0 4px 15px rgba(0, 0, 0, 0.05) !important;
  animation: pulse-active-glow 3s infinite ease-in-out;
}
@keyframes pulse-active-glow {
  0% {
    box-shadow: 0 0 12px rgba(124, 58, 237, 0.20), 0 4px 15px rgba(0, 0, 0, 0.04);
    border-color: #7c3aed;
  }
  50% {
    box-shadow: 0 0 24px rgba(124, 58, 237, 0.42), 0 6px 20px rgba(124, 58, 237, 0.12);
    border-color: #9333ea;
  }
  100% {
    box-shadow: 0 0 12px rgba(124, 58, 237, 0.20), 0 4px 15px rgba(0, 0, 0, 0.04);
    border-color: #7c3aed;
  }
}
.bg-purple-light-subtle {
  background-color: #f5f3ff !important;
}
.border-purple-active {
  border: 1px solid #ddd6fe !important;
}
.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}
.hover-underline:hover {
  text-decoration: underline;
}
.actor-chip {
  cursor: pointer;
  transition: transform 0.15s ease-in-out;
}
.actor-chip:hover {
  transform: scale(1.04);
}
.actor-menu-item {
  cursor: pointer;
}
.actor-menu-item:hover {
  background-color: #f3f4f9;
}
.actor-menu-list {
  max-height: 250px;
  overflow-y: auto;
}

/* High Contrast Badges & Chips */
.custom-actor-chip {
  background-color: #0284c7 !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  border-radius: 16px !important;
}
.custom-actor-chip >>> span,
.custom-actor-chip span,
.custom-actor-chip .v-chip__content {
  color: #ffffff !important;
}

.custom-more-chip {
  background-color: #334155 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
}
.custom-more-chip >>> span,
.custom-more-chip span,
.custom-more-chip .v-chip__content {
  color: #ffffff !important;
}

.custom-perimeter-chip {
  background-color: #6366f1 !important;
  color: #ffffff !important;
  font-weight: 600 !important;
}
.custom-perimeter-chip >>> span,
.custom-perimeter-chip span,
.custom-perimeter-chip .v-chip__content {
  color: #ffffff !important;
}

.custom-active-chip {
  background-color: #7c3aed !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3) !important;
}
.custom-active-chip >>> span,
.custom-active-chip span,
.custom-active-chip .v-chip__content {
  color: #ffffff !important;
}

.custom-inactive-chip {
  background-color: #64748b !important;
  color: #ffffff !important;
  font-weight: 600 !important;
}
.custom-inactive-chip >>> span,
.custom-inactive-chip span,
.custom-inactive-chip .v-chip__content {
  color: #ffffff !important;
}
</style>


